/**
 * src/utils/rankings.js
 *
 * Funções puras de ranking, extraídas para um utilitário próprio para
 * poderem ser reaproveitadas em qualquer relatório/tela (RelatorioMensal,
 * ficha de turma, etc.) sem duplicar lógica de ordenação.
 *
 * Nenhuma dessas funções acessa o Firestore — recebem os dados já
 * carregados e devolvem uma cópia ordenada. Isso facilita testar e
 * reaproveitar (a mesma função serve pra tela e pra um relatório em PDF,
 * por exemplo).
 */

/**
 * Soma um array de 4 posições (1 por trimestre). Se `trimestre` for
 * informado (0 a 3), retorna só aquela posição em vez da soma total.
 */
function somarHistorico(arr, trimestre) {
  if (!arr) return 0;
  if (trimestre === null || trimestre === undefined) {
    return arr.reduce((acc, val) => acc + (val || 0), 0);
  }
  return arr[trimestre] || 0;
}

/**
 * Ranking de alunos por pontos acumulados (historico.pontos).
 * `trimestre`: null = ano todo, 0-3 = só aquele trimestre.
 * `limite`: quantos primeiros colocados retornar (padrão: todos).
 */
export function rankearAlunosPorPontos(alunos, trimestre = null, limite = Infinity) {
  return [...alunos]
    .map((aluno) => ({
      id: aluno.id,
      nome: aluno.nome,
      turmaNome: aluno.turmaNome,
      valor: somarHistorico(aluno.historico?.pontos, trimestre),
    }))
    .sort((a, b) => b.valor - a.valor)
    .slice(0, limite);
}

/**
 * Ranking de alunos por presenças (historico.presenca).
 */
export function rankearAlunosPorPresenca(alunos, trimestre = null, limite = Infinity) {
  return [...alunos]
    .map((aluno) => ({
      id: aluno.id,
      nome: aluno.nome,
      turmaNome: aluno.turmaNome,
      valor: somarHistorico(aluno.historico?.presenca, trimestre),
    }))
    .sort((a, b) => b.valor - a.valor)
    .slice(0, limite);
}

/**
 * Ranking de turmas por percentual de frequência.
 *
 * Recebe uma lista de documentos `aulaTurma` já FECHADOS (situacao ===
 * "fechada"), cada um já com o campo `percentual` calculado no momento em
 * que a chamada foi enviada (ver AulaChamada.js). Se a turma teve mais de
 * uma aula no período, faz a MÉDIA dos percentuais de cada aula — não soma
 * matriculados/presentes de aulas diferentes, porque o nº de matriculados
 * pode mudar de uma aula pra outra (aluno novo, transferência etc.).
 */
export function rankearTurmasPorPercentual(aulaTurmas, limite = Infinity) {
  const porTurma = {};
  aulaTurmas
    .filter((at) => at.situacao === "fechada" && typeof at.percentual === "number")
    .forEach((at) => {
      if (!porTurma[at.nome]) porTurma[at.nome] = { nome: at.nome, soma: 0, qtd: 0 };
      porTurma[at.nome].soma += at.percentual;
      porTurma[at.nome].qtd += 1;
    });

  return Object.values(porTurma)
    .map((t) => ({ nome: t.nome, valor: Number((t.soma / t.qtd).toFixed(2)) }))
    .sort((a, b) => b.valor - a.valor)
    .slice(0, limite);
}

/**
 * Ranking de turmas por oferta total arrecadada no período (soma de
 * `totalOfertas` de todas as aulaTurma fechadas daquela turma).
 */
export function rankearTurmasPorOferta(aulaTurmas, limite = Infinity) {
  const porTurma = {};
  aulaTurmas
    .filter((at) => at.situacao === "fechada")
    .forEach((at) => {
      if (!porTurma[at.nome]) porTurma[at.nome] = 0;
      porTurma[at.nome] += Number(at.totalOfertas) || 0;
    });

  return Object.entries(porTurma)
    .map(([nome, valor]) => ({ nome, valor: Number(valor.toFixed(2)) }))
    .sort((a, b) => b.valor - a.valor)
    .slice(0, limite);
}
